import { defineStore } from 'pinia'
import _ from 'lodash'

import { authApi, resourceApi, azureApi } from './common/api'
import firebase from './common/firebase'
import {
  getRecords,
  rules,
  getAvatar,
  roleColors
} from './common/commonFunctions'
import { createIndexedDBAdapter } from './common/indexedDbStorage'

const roleApi = resourceApi.extend('role')

const clearLocalStorage = () => {
  const exceptedKeys = [`${__APP_NAME__}_version`]

  Object.keys(localStorage).forEach((key) => {
    if (!exceptedKeys.includes(key)) {
      localStorage.removeItem(key)
    }
  })
}

export const useAuthentication = defineStore('auth', {
  persist: [
    {
      key: (context) => `${__APP_NAME__}_${context}`,
      pick: ['rememberMe', 'role'],
      storage: localStorage,
      beforeHydrate: async ({ store }) => {
        if (store.isNewVersion) {
          await store.logout()
        }
      }
    }
  ],
  state() {
    return {
      isLogin: true,
      isRegister: false,
      rememberMe: true,
      user: null,
      provider: null,
      availableProviders: {
        password: 'Email & Password',
        aad: 'Microsoft',
        google: 'Google'
      },
      role: null,
      roles: null,
      institution: null,
      canAuthorScenariosFlag: false,
      canApproveScenariosFlag: false,
      fetchSessionFrequency: 3480000, // every 59 minutes because access tokens are valid for 1 hour,
      fetchSessionInterval: null,
      isForgotPassword: false,
      isLoadingAuthentication: false,
      errorAuthentication: null,
      statusAuthentication: null,
      isLoadingSetRole: false,
      errorSetRole: null,
      statusSetRole: null,
      rules: rules.auth,
      inviteData: {
        invite: null,
        linkId: null,
        roles: null,
        institution: null
      },
      registrationData: {
        email: null,
        provider: null,
        providerUserId: null,
        password: null,
        confirmPassword: null,
        name: null,
        username: null,
        isVerifyMobile: null,
        isOtpSent: null,
        otpCode: null,
        countryCode: null,
        mobile: null,
        mobileUid: null,
        picture: null
      },
      loginData: {
        email: null,
        password: null
      },
      forgotData: {
        email: null,
        get: null,
        password: null,
        confirmPassword: null,
        isSent: null,
        isGet: null
      },
      verificationData: null
    }
  },

  getters: {
    isAuthenticated: ({
      user,
      isLoadingAuthentication,
      errorAuthentication,
      statusAuthentication
    }) => {
      return (
        user != null &&
        isLoadingAuthentication == false &&
        errorAuthentication == null &&
        [200, null].includes(statusAuthentication)
      )
    },
    isLearner: ({
      user,
      role,
      isLoadingAuthentication,
      errorAuthentication,
      statusAuthentication
    }) => {
      return (
        user != null &&
        isLoadingAuthentication == false &&
        errorAuthentication == null &&
        [200, null].includes(statusAuthentication) &&
        role?.code == 'LRN'
      )
    },
    isDeveloper: ({
      user,
      role,
      isLoadingAuthentication,
      errorAuthentication,
      statusAuthentication
    }) => {
      return (
        user != null &&
        isLoadingAuthentication == false &&
        errorAuthentication == null &&
        [200, null].includes(statusAuthentication) &&
        role?.code == 'DEV'
      )
    },
    isInstructor: ({
      user,
      role,
      isLoadingAuthentication,
      errorAuthentication,
      statusAuthentication
    }) => {
      return (
        user != null &&
        isLoadingAuthentication == false &&
        errorAuthentication == null &&
        [200, null].includes(statusAuthentication) &&
        role?.code == 'INS'
      )
    },
    isAdmin: ({
      user,
      role,
      isLoadingAuthentication,
      errorAuthentication,
      statusAuthentication
    }) => {
      return (
        user != null &&
        isLoadingAuthentication == false &&
        errorAuthentication == null &&
        [200, null].includes(statusAuthentication) &&
        role?.code == 'ADM'
      )
    },
    isSuperAdmin: ({
      user,
      role,
      institution,
      isLoadingAuthentication,
      errorAuthentication,
      statusAuthentication
    }) => {
      return (
        user != null &&
        isLoadingAuthentication == false &&
        errorAuthentication == null &&
        [200, null].includes(statusAuthentication) &&
        role?.code == 'ADM' &&
        institution == null
      )
    },
    canAuthorScenarios: ({
      user,
      role,
      institution,
      canAuthorScenariosFlag,
      isLoadingAuthentication,
      errorAuthentication,
      statusAuthentication
    }) => {
      const authenticated =
        user != null &&
        isLoadingAuthentication == false &&
        errorAuthentication == null &&
        [200, null].includes(statusAuthentication)
      if (!authenticated) return false
      // Super-admins always get the tab so they can open authoring while configuring orgs.
      if (role?.code == 'ADM' && institution == null) return true
      return !!canAuthorScenariosFlag
    },
    canApproveScenarios: ({
      user,
      role,
      institution,
      canApproveScenariosFlag,
      isLoadingAuthentication,
      errorAuthentication,
      statusAuthentication
    }) => {
      const authenticated =
        user != null &&
        isLoadingAuthentication == false &&
        errorAuthentication == null &&
        [200, null].includes(statusAuthentication)
      if (!authenticated) return false
      // Super-admins manage approvals while configuring orgs.
      if (role?.code == 'ADM' && institution == null) return true
      return !!canApproveScenariosFlag
    },
    isDirectAccessInstitution: ({ institution }) => {
      return (
        institution != null &&
        (institution.directAccessEnabled === true ||
          institution.directAccessEnabled === 1 ||
          institution.directAccessEnabled === '1')
      )
    },
    isDirectAccessStaff: ({
      user,
      role,
      institution,
      isLoadingAuthentication,
      errorAuthentication,
      statusAuthentication
    }) => {
      return (
        user != null &&
        isLoadingAuthentication == false &&
        errorAuthentication == null &&
        [200, null].includes(statusAuthentication) &&
        ['ADM', 'INS'].includes(role?.code) &&
        institution != null &&
        (institution.directAccessEnabled === true ||
          institution.directAccessEnabled === 1 ||
          institution.directAccessEnabled === '1')
      )
    },
    canSeeAccessRoster: ({
      user,
      role,
      institution,
      isLoadingAuthentication,
      errorAuthentication,
      statusAuthentication
    }) => {
      const authenticated =
        user != null &&
        isLoadingAuthentication == false &&
        errorAuthentication == null &&
        [200, null].includes(statusAuthentication)
      if (!authenticated || !['ADM', 'INS'].includes(role?.code)) return false
      if (institution == null) return role?.code == 'ADM'
      return (
        institution.directAccessEnabled === true ||
        institution.directAccessEnabled === 1 ||
        institution.directAccessEnabled === '1'
      )
    },
    isAllowedProvider: ({ availableProviders }) => {
      return (provider) => _.keys(availableProviders).includes(provider)
    },
    getAvailableProviders: ({ availableProviders }) => {
      return availableProviders
    },
    getFetchSessionFrequency: ({ fetchSessionFrequency }) => {
      return fetchSessionFrequency
    },
    getFetchSessionInterval: ({ fetchSessionInterval }) => {
      return fetchSessionInterval
    },
    registrationStage: ({ user, inviteData, registrationData, isRegister }) => {
      if (!isRegister || user != null) return 3
      if (
        inviteData !== null &&
        inviteData.invite != null &&
        inviteData.roles != null
      )
        return registrationData.isVerifyMobile == true ? 2 : 1
      return 0
    },
    getUserName: ({ user }) => {
      return user?.name || 'Account'
    },
    getUser: ({ user }) => {
      return user == null
        ? null
        : Object.fromEntries(
            Object.entries(user).filter(([k]) =>
              [
                'id',
                'provider',
                'countryCode',
                'createTs',
                'updateTs',
                'deleteTs'
              ].includes(k)
            )
          )
    },
    getInstitution: ({ institution }) => {
      return institution
    },
    getAvatar: ({ user }) => {
      return getAvatar(user)
    },
    getProvider: ({ provider }) => {
      return provider
    },
    getInviteCode: ({ inviteData }) => {
      return inviteData?.invite?.code
    },
    getRegistrationName: ({ registrationData }) => {
      return registrationData.name
    },
    getErrorAuthentication: ({ errorAuthentication }) => {
      return errorAuthentication
    },
    getCurrentRole: ({ role }) => {
      return role
    },
    getRoleName: ({ role }) => {
      return role?.name
    },
    getRoleColors: ({ role }) => {
      return roleColors[role.code]
    },
    getRolesByCode: ({ roles }) =>
      _.keyBy(
        _.map(roles, (r, index) => ({ ...r, index })),
        'code'
      ),
    getIsLoadingAuthentication: ({ isLoadingAuthentication }) => {
      isLoadingAuthentication
    },
    getIsLoadingMessage: ({ isLoadingSetRole }) => {
      if (isLoadingSetRole) return 'Switching the user\'s role...'
    },
    getErrors: ({ errorSetRole }) => {
      let returnValue = []
      if (errorSetRole)
        returnValue.push({
          text: errorSetRole,
          key: 'setRole'
        })
      return returnValue
    }
  },

  actions: {
    setCanAuthorScenarios(flag) {
      this.canAuthorScenariosFlag = !!flag
    },
    setCanApproveScenarios(flag) {
      this.canApproveScenariosFlag = !!flag
    },
    confirmPassword(confirmValue, passwordValue) {
      if (confirmValue != null)
        confirmValue = confirmValue.replace(/^\s+|\s+$/g, '')
      if (passwordValue != null)
        passwordValue = passwordValue.replace(/^\s+|\s+$/g, '')
      if (confirmValue == passwordValue) return true
      return 'Password confirmation mismatch.'
    },
    async logout(newState = 'isLogin', withLoader = true) {
      const url = '/logout'
      if (this.errorAuthentication != null) this.errorAuthentication = null
      this.statusAuthentication = null
      if (withLoader) this.isLoadingAuthentication = true
      try {
        const result = await authApi.get(url)
        const { error, provider } = result.data.response
        this.statusAuthentication = result.data.status
        if (error) this.errorAuthentication = error.message
        else if (this.statusAuthentication == 200) {
          this.user = null
          this.role = null
          this.institution = null
          this.provider = null
          if (provider == 'aad') window.location.href = '/.auth/logout'
        }
      } catch (e) {
        this.user = null
        this.role = null
        this.institution = null
        this.provider = null
        this.errorAuthentication =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
        this.statusAuthentication = e?.status
      }
      if (newState != null) this.setState(newState)
      this.isLoadingAuthentication = false
      clearLocalStorage()
      const indexedDBs = [
        ['simulation', 'hero'],
        ['resource', 'files'],
        ['download', 'jobs'],
        ['firebase-heartbeat-database', 'firebase-heartbeat-store'],
        ['firebaseLocalStorageDb', 'firebaseLocalStorage']
      ]
      for (const indexedDB of indexedDBs)
        createIndexedDBAdapter(...indexedDB).clear()
      if (!window.location.pathname.startsWith('/demo/'))
        this.router.push('/welcome')
    },
    async verifyProvider(provider, withLoader = false, authorization = null) {
      if (this.errorAuthentication != null) this.errorAuthentication = null
      this.statusAuthentication = null
      if (withLoader) this.isLoadingAuthentication = true
      try {
        const url = 'verify'
        const result = await authApi.get(url, {
          params: { provider, authorization }
        })
        const { error, user } = result.data.response
        this.statusAuthentication = result.data.status
        if (error) {
          this.errorAuthentication = error.message
        } else this.verificationData = user
      } catch (e) {
        this.errorAuthentication =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
        this.statusAuthentication = e?.status
      }
      this.isLoadingAuthentication = false
    },
    async fetchSession(withLoader = false) {
      if (this.errorAuthentication != null) this.errorAuthentication = null
      this.statusAuthentication = null
      if (withLoader) this.isLoadingAuthentication = true
      const url = '/session'
      try {
        const result = await authApi.get(url)
        const { error, user, roles, role, institution } = result.data.response
        this.statusAuthentication = result.data.status
        if (error) this.errorAuthentication = error.message
        else {
          this.user = user
          this.roles = roles
          this.role = role
          this.institution = institution
          this.provider = user?.provider
        }
      } catch (e) {
        this.user = null
        this.role = null
        this.institution = null
        this.provider = null
        this.errorAuthentication =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
        this.statusAuthentication = e?.status
      }
      this.isLoadingAuthentication = false
    },
    async invite(
      inviteCode,
      provider = 'password',
      withLoader = true,
      event = null
    ) {
      provider = provider.replace(/^\s+|\s+$/g, '')
      if (!this.isAllowedProvider(provider))
        this.errorAuthentication = `Unsupported authentication provider: "${provider}".`
      else {
        if (this.errorAuthentication != null) this.errorAuthentication = null
        this.statusAuthentication = null
        if (withLoader) this.isLoadingAuthentication = true
        try {
          let error = null
          let invite = null
          let linkId = null
          let roles = null
          let institution = null
          if (this.inviteData.invite == null || this.inviteData.roles == null) {
            const url = `/invite?code=${inviteCode}`
            const result = await authApi.get(url)
            ;({ error, invite, linkId, roles, institution } =
              result.data.response)
            this.statusAuthentication = result.data.status
            if (error != null) this.errorAuthentication = error.message
          }
          if (error == null) {
            if (provider != 'password' && this.verificationData == null) {
              if (provider == 'google')
                await this.verifyProvider('google', false, event.code)
              else await this.verifyProvider(provider)
              const email = this.inviteData.invite?.email || invite?.email
              if (this.errorAuthentication != null) {
                if (['aad'].includes(provider)) window.location.href = '/.auth/logout'
              } else if (this.verificationData.email != email) {
                if (['aad'].includes(provider)) window.location.href = '/.auth/logout'
                this.errorAuthentication = 'Sign-up invite email mismatch.'
              } else {
                for (const k of _.keys(this.verificationData))
                  this.registrationData[k] = this.verificationData[k]
              }
              this.verificationData = null
            }
            if (
              this.inviteData.invite == null ||
              this.inviteData.roles == null
            ) {
              this.inviteData = { invite, linkId, roles, institution }
              this.setState('isRegister')
            }
          }
        } catch (e) {
          this.inviteData = {
            invite: null,
            role: null,
            institution: null
          }
          this.errorAuthentication =
            e?.response?.data?.response?.error?.message ||
            e.message ||
            String(e) ||
            JSON.stringify(e)
          this.statusAuthentication = e?.status
          if (['aad'].includes(provider)) window.location.href = '/.auth/logout'
        }
        this.isLoadingAuthentication = false
      }
    },
    async register(provider, withLoader = true) {
      if (this.registrationData?.mobileUid == null)
        this.errorAuthentication =
          'The registration can’t be completed without a phone verification.'
      else {
        if (this.errorAuthentication != null) this.errorAuthentication = null
        this.statusAuthentication = null
        if (withLoader) this.isLoadingAuthentication = true
        provider = provider.replace(/^\s+|\s+$/g, '')
        if (this.user != null) this.user = null
        if (this.provider != null) this.provider = null
        try {
          if (!['password', 'aad', 'google'].includes(provider)) {
            this.errorAuthentication = `Unsupported registration provider: ${provider}.`
          } else {
            this.provider = provider
            const url = '/register'
            const payload = {
              user: {
                email: this.inviteData.invite.email.replace(/^\s+|\s+$/g, ''),
                emailVerified: true,
                institutionId: this.inviteData?.institution?.id || null,
                provider: this.provider,
                providerUserId: this.registrationData.providerUserId,
                name: this.registrationData.name.trim(),
                username: this.registrationData.username?.trim() || null,
                countryCode: this.registrationData.countryCode,
                mobileUid: this.registrationData.mobileUid,
                mobileVerified: true,
                picture: this.registrationData.picture
              },
              institution: this.inviteData.institution,
              invite: this.inviteData.invite,
              linkId: this.inviteData.linkId
            }
            if (this.provider == 'password') {
              payload.user.password = this.registrationData.password.trim()
            }
            const result = await authApi.post(url, payload, {
              params: { provider: this.provider }
            })
            const { error, user, roles, role, institution } = result.data.response
            this.statusAuthentication = result.data.status
            if (error) {
              this.errorAuthentication = error.message
              if (this.provider == 'aad') window.location.href = '/.auth/logout'
            } else {
              this.user = user
              this.roles = roles
              this.role = role
              this.institution = institution
              this.rememberMe = true
              this.registrationData = _.mapValues(
                this.registrationData,
                () => null
              )
              this.inviteData = _.mapValues(this.inviteData, () => null)
              this.setState()
              this.router.push('/')
            }
          }
        } catch (e) {
          this.errorAuthentication =
            e?.response?.data?.response?.error?.message ||
            e.message ||
            String(e) ||
            JSON.stringify(e)
          this.statusAuthentication = e?.status
          if (['aad'].includes(provider)) window.location.href = '/.auth/logout'
        }
        this.isLoadingAuthentication = false
      }
    },
    async login(provider, event = null, withLoader = true) {
      provider = provider.replace(/^\s+|\s+$/g, '')
      if (!this.isAllowedProvider(provider))
        this.errorAuthentication = `Unsupported authentication provider: "${provider}".`
      else {
        if (withLoader) this.isLoadingAuthentication = true
        this.errorAuthentication = null
        this.statusAuthentication = null
        const url = '/login'
        let params = {
          remember: !this.rememberMe ? '0' : '1',
          provider
        }
        this.user = null
        let payload = {}
        this.provider = provider
        try {
          if (this.provider == 'password') {
            const form = event != null ? await event : { valid: true }
            if (!form.valid)
              this.errorAuthentication = 'Invalid email or password.'
            else
              payload = {
                email: this.loginData.email.trim(),
                password: this.loginData.password.trim()
              }
          } else if (this.provider == 'google') {
            await this.verifyProvider('google', false, event.code)
            if (this.errorAuthentication == null)
              payload = {
                email: this.verificationData?.email,
                providerUserId: this.verificationData?.providerUserId,
                picture: this.verificationData?.picture
              }
          }
          if (this.errorAuthentication == null) {
            const result = await authApi.post(url, payload, { params })
            const { error, user, roles, role, institution } =
              result.data.response

            this.statusAuthentication = result.data.status

            if (error) {
              this.errorAuthentication = error.message
            } else {
              this.user = user
              this.roles = roles
              this.role = role
              this.institution = institution
              this.router.push('/')
            }
          } else this.provider = null
        } catch (e) {
          this.errorAuthentication =
            e?.response?.data?.response?.error?.message ||
            e.message ||
            String(e) ||
            JSON.stringify(e)
          this.statusAuthentication = e?.status
          if (['aad'].includes(this.provider)) window.location.href = '/.auth/logout'
          this.provider = null
        }
        if (
          this.provider == 'password' &&
          [200, 401].includes(this.statusAuthentication)
        )
          this.loginData = _.mapValues(this.loginData, () => null)
        this.isLoadingAuthentication = false
      }
    },
    setFetchSessionInterval(newValue) {
      if (newValue == null && this.fetchSessionInterval != null)
        clearInterval(this.fetchSessionInterval)
      this.fetchSessionInterval = newValue
    },
    setDirectAccessEnabled(value) {
      if (this.institution == null) return
      this.institution = {
        ...this.institution,
        directAccessEnabled: !!value
      }
    },
    setRegistrationCountryCode(newValue) {
      this.registrationData.countryCode = newValue
    },
    async registerLearner() {
      this.isLoadingAuthentication = true
      if (this.errorAuthentication != null) this.errorAuthentication = null
      try {
        const payload = {
          user: {
            email: this.inviteData.invite.email.replace(/^\s+|\s+$/g, ''),
            emailVerified: false,
            institutionId: this.inviteData?.institution?.id || null,
            provider: 'learner',
            providerUserId: null,
            name: this.registrationData.username?.trim() || null,
            username: this.registrationData.username?.trim() || null,
            countryCode: null,
            mobileUid: null,
            mobileVerified: false,
            picture: null
          },
          institution: this.inviteData.institution,
          invite: this.inviteData.invite,
          linkId: this.inviteData.linkId
        }
        const result = await authApi.post('/register', payload, { params: { provider: 'learner' } })
        const { error, user, roles, role, institution } = result.data.response
        this.statusAuthentication = result.data.status
        if (error) {
          this.errorAuthentication = error.message
        } else {
          this.user = user
          this.roles = roles
          this.role = role
          this.institution = institution
          this.rememberMe = true
          this.registrationData = _.mapValues(this.registrationData, () => null)
          this.inviteData = _.mapValues(this.inviteData, () => null)
          this.setState()
          this.router.push('/')
        }
      } catch (e) {
        this.errorAuthentication =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingAuthentication = false
    },
    async setRegistrationVerifyMobile(newValue, eventData) {
      this.isLoadingAuthentication = true
      if (!newValue) this.registrationData.isVerifyMobile = newValue
      else {
        const { name, event } = eventData
        const form = await event
        if (form.valid) {
          this.registrationData.name = name
          this.registrationData.isVerifyMobile = newValue
        }
      }
      this.isLoadingAuthentication = false
    },
    async sendOtp(event, elementId = 'phone-verify') {
      const form = await event
      if (form.valid || form.isTrusted) {
        this.isLoadingAuthentication = true
        const setupRecaptcha = async (elementId) => {
          const recaptchaVerifier = new firebase.RecaptchaVerifier(
            firebase.auth,
            elementId,
            {
              size: 'invisible'
            }
          )
          const recaptchaWidgetId = await recaptchaVerifier.render()
          return { recaptchaVerifier, recaptchaWidgetId }
        }
        try {
          const { recaptchaVerifier } = await setupRecaptcha(elementId)
          this.registrationData.isOtpSent =
            await firebase.signInWithPhoneNumber(
              firebase.auth,
              this.registrationData.mobile,
              recaptchaVerifier
            )
        } catch (e) {
          if (e.name == 'FirebaseError' && e.code.startsWith('auth/'))
            this.errorAuthentication = e.message
              .replace(/^Firebase: Error \(auth\/(.*)\)\./, 'Firebase $1')
              .replace(/-/g, ' ')
          else this.errorAuthentication = e.message
        }
        this.isLoadingAuthentication = false
      }
    },
    async verifyOtp(event) {
      const form = await event
      if (form.valid || form.isTrusted) {
        if (this.errorAuthentication != null) this.errorAuthentication = null
        this.statusAuthentication = null
        this.isLoadingAuthentication = true
        try {
          const firebaseUser = await this.registrationData.isOtpSent.confirm(
            this.registrationData.otpCode
          )
          this.registrationData.mobileUid = firebaseUser?.user?.uid
          if (
            _.isObjectLike(firebaseUser) &&
            !_.isEmpty(firebaseUser) &&
            _.has(firebaseUser, 'user')
          ) {
            await firebase.signOut(firebase.auth)
            const provider =
              this.registrationData.provider == null
                ? 'password'
                : this.registrationData.provider
            await this.register(provider, false)
          }
        } catch (e) {
          if (e.name == 'FirebaseError' && e.code.startsWith('auth/'))
            this.errorAuthentication = e.message
              .replace(/^Firebase: Error \(auth\/(.*)\)\./, 'Firebase $1')
              .replace(/-/g, ' ')
          else this.errorAuthentication = e.message
        }
        this.isLoadingAuthentication = false
      }
    },
    async sendForgotPassword(event) {
      const form = await event
      if (form.valid || form.isTrusted) {
        if (this.errorAuthentication != null) this.errorAuthentication = null
        this.statusAuthentication = null
        this.isLoadingAuthentication = true
        try {
          const stopSend = await getRecords(
            'user',
            { where: { email: this.forgotData.email } },
            false,
            true,
            true
          )
          if (stopSend) this.errorAuthentication = 'User not found.'
          else {
            const url = '/password?send'
            const payload = {
              email: this.forgotData.email,

              origin: location.origin
            }
            const result = await authApi.post(url, payload)
            const { error, send } = result.data.response
            this.statusAuthentication = result.data.status
            if (error) {
              this.errorAuthentication = error.message
            } else {
              this.forgotData.isSent = send
            }
          }
        } catch (e) {
          this.errorAuthentication =
            e?.response?.data?.response?.error?.message ||
            e.message ||
            String(e) ||
            JSON.stringify(e)
          this.statusAuthentication = e?.status
        }
        this.isLoadingAuthentication = false
      }
    },
    async getForgotPassword(resetCode) {
      if (this.errorAuthentication != null) this.errorAuthentication = null
      this.statusAuthentication = null
      this.isLoadingAuthentication = true
      const url = `password?get&code=${resetCode}`
      try {
        const result = await authApi.get(url)
        const { error, user, password, get } = result.data.response
        this.statusAuthentication = result.data.status
        if (error) {
          this.errorAuthentication = error.message
        } else {
          this.forgotData.isGet = get
          this.forgotData.get = { user, password }
          this.toggleIsForgotPassword()
        }
      } catch (e) {
        this.errorAuthentication =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
        this.statusAuthentication = e?.status
      }
      this.isLoadingAuthentication = false
    },
    async changePassword(event) {
      const form = await event
      if (form.valid || form.isTrusted) {
        if (this.errorAuthentication != null) this.errorAuthentication = null
        this.statusAuthentication = null
        this.isLoadingAuthentication = true
        const url = '/password?reset'
        const payload = {
          password: this.forgotData.password,
          user: this.forgotData.get.user.id,
          reset: this.forgotData.get.password.id
        }
        try {
          const result = await authApi.post(url, payload)
          const { error, reset } = result.data.response
          this.statusAuthentication = result.data.status
          if (error) {
            this.errorAuthentication = error.message
          } else if (reset) {
            this.loginData.email = this.forgotData.get.user.email
            this.loginData.password = this.forgotData.password
            await this.login('password', null, false)
            this.toggleIsForgotPassword()
          }
        } catch (e) {
          this.errorAuthentication =
            e?.response?.data?.response?.error?.message ||
            e.message ||
            String(e) ||
            JSON.stringify(e)
          this.statusAuthentication = e?.status
        }
        this.isLoadingAuthentication = false
      }
    },
    toggleIsForgotPassword() {
      this.isForgotPassword = !this.isForgotPassword
      if (!this.isForgotPassword) {
        this.forgotData = _.mapValues(this.forgotData, () => null)
      }
    },
    async setRole(newCode) {
      const role =
        newCode != null
          ? this.roles?.[this.getRolesByCode?.[newCode]?.index]
          : null
      if (role != null && _.has(role, 'code')) {
        this.errorSetRole = null
        this.isLoadingSetRole = true
        this.statusSetRole = null
        try {
          const result = await roleApi.head('', { params: { code: role.code } })
          this.statusSetRole = _.has(result.headers, 'x-status')
            ? result.headers['x-status']
            : result.status
          if (
            _.has(result.headers, 'x-error') &&
            result.headers['x-error'] == 'true'
          )
            this.errorSetRole = result.headers['x-error-message']
          else this.role = role
        } catch (e) {
          this.errorSetRole =
            e?.response?.data?.response?.error?.message ||
            e.message ||
            String(e) ||
            JSON.stringify(e)
        }
        this.isLoadingSetRole = false
      }
    },
    setState(state, newValue = true) {
      switch (state) {
        case 'isLogin':
          this.isLogin = newValue
          this.isRegister = !newValue
          break
        case 'isRegister':
          this.isLogin = !newValue
          this.isRegister = newValue
          break
        default:
          this.isLogin = newValue
          this.isRegister = !newValue
      }
    },
    setErrorAuthentication(newValue) {
      this.errorAuthentication = newValue
    },
    setStatusAuthentication(newValue) {
      this.statusAuthenticatio = newValue
    },
    nullifyInviteData() {
      this.inviteData = {
        invite: null,
        role: null,
        institution: null
      }
    },
    cancelErrorAuthentication() {
      this.errorAuthentication = null
    },
    cancelError(key) {
      switch (key) {
        case 'setRole':
          this.errorSetRole = null
          break
        default:
          break
      }
    }
  }
})
